// ─── Admin Tab: Supervisor Price-Change Approvals (تأكيد تغيير الأسعار) ───
// Shows pending price-change requests from supervisors. Admin reviews
// product / old → new / supervisor name and approves or rejects.
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ShieldCheck,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock3,
  User,
  Package,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCENT = "#7C3AED";
const fmt = (n) => Number(n || 0).toLocaleString("en-US");

const FILTERS = [
  { id: "pending", label: "بانتظار التأكيد" },
  { id: "approved", label: "مُوافق عليه" },
  { id: "rejected", label: "مرفوض" },
  { id: "all", label: "الكل" },
];

const SupervisorPriceApprovalsTab = () => {
  const [filter, setFilter] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [reconciling, setReconciling] = useState(false);

  const reconcileSupervisorSales = async () => {
    setReconciling(true);
    try {
      const { data } = await axios.post(`${API}/admin/reconcile-supervisor-sales`);
      toast.success(data.message || `تم إصلاح ${data.fixed_count} فاتورة`);
      if (data.fixed_count > 0) {
        console.log("Fixed supervisor sales:", data.fixed);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل الإصلاح");
    } finally {
      setReconciling(false);
    }
  };

  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await axios.get(`${API}/admin/supervisor-price-approvals`, { params });
      setRows(data || []);
    } catch {
      toast.error("فشل تحميل قائمة طلبات التأكيد");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { queueMicrotask(() => { fetchRows(); }); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0 };
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  const decide = async (id, status) => {
    setBusyId(id);
    try {
      await axios.patch(`${API}/admin/supervisor-price-approvals/${id}`, { status });
      toast.success(status === "approved" ? "تمّت الموافقة على السعر الجديد" : "تمّ رفض الطلب");
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="sup-price-approvals-tab" dir="rtl">
      <div className="text-white rounded-2xl p-4 shadow-md" style={{ background: `linear-gradient(135deg, #4C1D95, ${ACCENT})` }}>
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-5 h-5" />
          <h3 className="font-black text-lg">تأكيد تغيير الأسعار من المشرفين</h3>
        </div>
        <p className="text-xs opacity-95">المشرفون يطلبون تعديل السعر — وأنت توافق أو ترفض. لا يتغيّر أي سعر قبل موافقتك.</p>
      </div>

      {/* ── Reconcile supervisor sales — fixes legacy orders that bypassed
          the supervisor-approval stock decrement (bug fixed Feb 14, 2026). */}
      <div className="bg-gradient-to-l from-[#FEF3C7] to-[#FDE68A] border-2 border-[#F59E0B] rounded-2xl p-3" data-testid="reconcile-sup-sales-card">
        <div className="flex items-start gap-2 mb-2">
          <div className="bg-[#F59E0B] text-white rounded-full p-1.5 shrink-0">
            <Package className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-black text-[#7c2d12]">إصلاح بيوع المشرفين القديمة</p>
            <p className="text-[11px] text-[#7c2d12]/80 mt-0.5">
              لو سويت فواتير من مشرف قبل صار التحديث، وصارت كميات ما انخصمت من المخزن — اضغط الزرّ هنا
              مرّة وحدة عشان نصلّحها تلقائياً (إيداعات المخزن + ديون الزبون). الإجراء آمن وممكن تستدعيه أكثر من مرة.
            </p>
          </div>
        </div>
        <button
          onClick={reconcileSupervisorSales}
          disabled={reconciling}
          data-testid="reconcile-sup-sales-btn"
          className="w-full bg-[#F59E0B] hover:bg-[#D97706] disabled:opacity-60 text-white font-bold py-2 rounded-xl flex items-center justify-center gap-2 text-sm transition-colors"
        >
          {reconciling ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          {reconciling ? "جاري الإصلاح..." : "إصلاح المخزن وديون الزبائن الآن"}
        </button>
      </div>

      <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6] flex gap-1 bg-[#F3F6F4] flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            data-testid={`sup-price-filter-${f.id}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${filter === f.id ? "text-white" : "text-[#5C6E64] hover:bg-white"}`}
            style={filter === f.id ? { background: ACCENT } : undefined}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-10 text-center"><Loader2 className="w-7 h-7 animate-spin inline" style={{ color: ACCENT }} /></div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl p-8 text-center text-sm text-[#5C6E64]">
          <ShieldCheck className="w-10 h-10 mx-auto mb-2 opacity-50" />
          لا توجد طلبات ضمن هذا التصنيف.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} data-testid={`sup-price-row-${r.id}`} className="bg-white rounded-2xl border-2 border-[#E4EBE6] p-3 space-y-2">
              <div className="flex justify-between items-start flex-wrap gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-black text-sm text-[#1E3A5F] flex items-center gap-1 truncate">
                    <Package className="w-4 h-4 text-[#5C6E64]" /> {r.product_name}
                  </p>
                  <p className="text-[10px] text-[#5C6E64] flex items-center gap-1 mt-0.5">
                    <User className="w-3 h-3" /> المشرف: <b>{r.supervisor_name}</b>
                    <span dir="ltr" className="opacity-70">· {r.supervisor_phone}</span>
                  </p>
                </div>
                {r.status === "pending" ? (
                  <span className="bg-[#FEF3C7] text-[#92400e] text-[10px] font-bold rounded-full px-2 py-0.5 flex items-center gap-1"><Clock3 className="w-3 h-3" /> بانتظار التأكيد</span>
                ) : r.status === "approved" ? (
                  <span className="bg-[#DCFCE7] text-[#15803d] text-[10px] font-bold rounded-full px-2 py-0.5 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> مُوافق</span>
                ) : (
                  <span className="bg-[#FEE2E2] text-[#dc2626] text-[10px] font-bold rounded-full px-2 py-0.5 flex items-center gap-1"><XCircle className="w-3 h-3" /> مرفوض</span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="bg-[#FEF2F2] rounded-xl p-2 text-center">
                  <p className="text-[10px] text-[#5C6E64]">السعر القديم</p>
                  <p className="font-black text-xl text-[#5C6E64] line-through" dir="ltr">{fmt(r.old_price)}</p>
                </div>
                <div className="bg-[#F0FDF4] rounded-xl p-2 text-center border-2 border-[#7ED321]/40">
                  <p className="text-[10px] text-[#15803d]">السعر المطلوب</p>
                  <p className="font-black text-xl text-[#15803d]" dir="ltr">{fmt(r.new_price)}</p>
                </div>
              </div>

              {r.note && (
                <div className="bg-[#F8FAFC] rounded-xl p-2 text-xs">
                  <p className="text-[9px] text-[#5C6E64] mb-0.5">سبب التعديل من المشرف:</p>
                  <p className="text-[#111814]">{r.note}</p>
                </div>
              )}

              {r.status === "pending" && (
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => decide(r.id, "approved")}
                    disabled={busyId === r.id}
                    data-testid={`sup-price-approve-${r.id}`}
                    className="flex-1 bg-[#15803d] hover:bg-[#0e6b30] disabled:opacity-50 text-white text-sm font-bold rounded-full py-2 flex items-center justify-center gap-1"
                  >
                    <CheckCircle2 className="w-4 h-4" /> موافقة
                  </button>
                  <button
                    onClick={() => decide(r.id, "rejected")}
                    disabled={busyId === r.id}
                    data-testid={`sup-price-reject-${r.id}`}
                    className="flex-1 bg-[#dc2626] hover:bg-[#b91c1c] disabled:opacity-50 text-white text-sm font-bold rounded-full py-2 flex items-center justify-center gap-1"
                  >
                    <XCircle className="w-4 h-4" /> رفض
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SupervisorPriceApprovalsTab;
